import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiDownload, FiCheckCircle, FiBarChart2, FiShield, FiAlertOctagon } = FiIcons;

const BackgroundEffects = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none bg-jeruel-primary">
    {/* High-quality industrial background image with blend mode */}
    <img 
      src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80&w=2000" 
      alt="Industrial Factory Background" 
      className="absolute inset-0 w-full h-full object-cover opacity-20 mix-blend-overlay"
    />
    
    <div className="absolute inset-0 bg-gradient-to-br from-jeruel-primary/95 via-jeruel-primary/90 to-jeruel-footer/95" />
    
    <svg className="absolute inset-0 w-full h-full opacity-[0.05]" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="brand-grid" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#FFFFFF" strokeWidth="1" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#brand-grid)" />
    </svg>
    <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-white/10 rounded-full blur-[100px]" />
    <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] bg-jeruel-light/20 rounded-full blur-[80px]" />
  </div>
);

const StatBadge = ({ value, label, delay }) => (
  <motion.div 
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    className="flex flex-col border-l-2 border-jeruel-soft/30 pl-4 py-1"
  >
    <span className="text-2xl sm:text-3xl font-bold text-white tracking-tight">{value}</span>
    <span className="text-xs sm:text-sm font-medium text-jeruel-soft mt-0.5 uppercase tracking-wider">{label}</span>
  </motion.div>
);

const ExecutiveReportVisual = () => (
  <div className="relative w-full max-w-lg mx-auto lg:ml-auto perspective-1000">
    <motion.div 
      initial={{ opacity: 0, rotateY: 10, rotateX: 5, x: 20 }}
      animate={{ opacity: 1, rotateY: -2, rotateX: 2, x: 0 }}
      transition={{ duration: 1, type: "spring", bounce: 0.4 }}
      className="relative z-10 w-full aspect-[4/5] bg-white rounded-xl border border-jeruel-gray shadow-2xl shadow-jeruel-footer/50 p-6 sm:p-8 flex flex-col justify-between overflow-hidden"
    >
      <div>
        <div className="flex justify-between items-start mb-6">
          <div className="w-12 h-12 rounded-lg bg-jeruel-offwhite flex items-center justify-center border border-jeruel-gray">
            <SafeIcon icon={FiBarChart2} className="text-2xl text-jeruel-primary" />
          </div>
          <span className="px-3 py-1 rounded-md bg-red-50 border border-red-100 text-red-600 text-[10px] font-bold uppercase tracking-widest">
            Executive Summary
          </span>
        </div>
        
        <div className="space-y-3">
          <div className="h-2 w-1/3 bg-jeruel-gray rounded-full" />
          <div className="h-3 w-3/4 bg-jeruel-dark rounded-full" />
          <div className="h-3 w-1/2 bg-gray-400 rounded-full" />
        </div>
      </div>

      <div className="relative h-40 w-full mt-8 border-b-2 border-jeruel-gray flex items-end justify-between gap-3 px-2 pb-2">
        {[30, 45, 25, 75, 90, 65, 85].map((height, i) => {
          const isExposed = height > 60;
          return (
            <motion.div 
              key={i}
              initial={{ height: 0 }}
              animate={{ height: `${height}%` }}
              transition={{ duration: 1, delay: 0.5 + (i * 0.1), ease: "easeOut" }}
              className={`w-full rounded-t-sm ${isExposed ? 'bg-red-500' : 'bg-jeruel-primary'}`}
            />
          );
        })}
        <div className="absolute top-[40%] left-0 w-full border-t-2 border-dashed border-red-300" />
        <span className="absolute top-[32%] right-0 text-[9px] font-bold text-red-500 bg-white px-1">Risk Threshold</span>
      </div>

      <div className="mt-6 pt-4 border-t border-jeruel-gray">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-jeruel-soft flex items-center justify-center border border-jeruel-light">
            <span className="text-jeruel-primary text-[10px] font-bold">19</span>
          </div>
          <div className="space-y-1.5">
            <div className="h-2 w-24 bg-gray-300 rounded-full" />
            <div className="h-1.5 w-16 bg-gray-200 rounded-full" />
          </div>
        </div>
      </div>
    </motion.div>

    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: [0, -8, 0] }}
      transition={{ opacity: { duration: 0.5, delay: 0.8 }, y: { duration: 4, repeat: Infinity, ease: "easeInOut" } }}
      className="absolute -right-4 sm:-right-8 top-1/4 z-20 bg-white border border-jeruel-gray rounded-lg p-3 shadow-xl"
    >
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-md bg-red-50 flex items-center justify-center text-red-500">
          <SafeIcon icon={FiAlertOctagon} />
        </div>
        <div>
          <p className="text-[9px] font-bold text-gray-500 uppercase">High Exposure</p>
          <p className="text-jeruel-dark font-bold text-sm">Planner: 7/10</p>
        </div>
      </div>
    </motion.div>

    <motion.div 
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: [0, 8, 0] }}
      transition={{ opacity: { duration: 0.5, delay: 1 }, y: { duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 } }}
      className="absolute -left-4 sm:-left-8 bottom-1/4 z-20 bg-white border border-jeruel-gray rounded-lg p-3 shadow-xl"
    >
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-md bg-emerald-50 flex items-center justify-center text-emerald-600">
          <SafeIcon icon={FiShield} />
        </div>
        <div>
          <p className="text-[9px] font-bold text-gray-500 uppercase">Protected Role</p>
          <p className="text-jeruel-dark font-bold text-sm">Mechanic: 2/10</p>
        </div>
      </div>
    </motion.div>
  </div>
);

const Hero = () => {
  return (
    <section className="relative min-h-[90vh] flex items-center pt-28 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <BackgroundEffects />
      
      <div className="relative z-10 max-w-7xl mx-auto w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          
          <div className="max-w-2xl mx-auto lg:mx-0 text-center lg:text-left">
            <motion.div 
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs sm:text-sm font-bold text-white mb-8 backdrop-blur-sm tracking-wide"
            >
              <span className="w-2 h-2 rounded-full bg-blue-300 animate-pulse" />
              Essential Workforce Intelligence for M&R Leaders
            </motion.div>

            <motion.h1 
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.05 }}
              className="text-4xl sm:text-5xl lg:text-[3.5rem] font-bold tracking-tight mb-6 text-white leading-[1.1]"
            >
              AI is reshaping maintenance teams.
              <span className="block mt-2 text-jeruel-soft">
                Do you have the blueprint?
              </span>
            </motion.h1>

            <motion.p 
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-base sm:text-xl font-medium mb-10 text-blue-100 leading-relaxed max-w-xl mx-auto lg:mx-0"
            >
              We mapped all 19 Maintenance & Reliability roles to Andrej Karpathy's AI exposure framework. The data shows that your wrench-turners are safe. <span className="text-white font-bold">However, your planners, engineers, and administrators are highly exposed.</span>
            </motion.p>

            <motion.div 
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="flex flex-col sm:flex-row items-center gap-5 justify-center lg:justify-start"
            >
              <a
                href="#download-form"
                className="group relative inline-flex items-center justify-center gap-3 px-8 py-4 sm:px-10 sm:py-5 text-lg font-bold rounded-xl text-jeruel-primary bg-white hover:bg-jeruel-offwhite transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1 w-full sm:w-auto overflow-hidden border border-white"
              >
                <span className="relative z-10">Download the Free Report</span>
                <SafeIcon icon={FiDownload} className="relative z-10 text-xl transform group-hover:translate-y-0.5 transition-transform" />
              </a>
              
              <div className="flex flex-col items-center sm:items-start gap-1 text-sm text-jeruel-soft font-medium">
                <div className="flex items-center gap-1.5 text-white">
                  <SafeIcon icon={FiCheckCircle} />
                  <span>100% Free Access</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <SafeIcon icon={FiCheckCircle} />
                  <span>Instant PDF Delivery</span>
                </div>
              </div>
            </motion.div>

            <div className="mt-12 flex justify-center lg:justify-start gap-8 sm:gap-12 w-full">
              <StatBadge value="25" label="Pages of Analysis" delay={0.2} />
              <StatBadge value="19" label="Roles Scored" delay={0.3} />
              <StatBadge value="143M" label="US Jobs Context" delay={0.4} />
            </div>
          </div>

          <div className="mt-10 lg:mt-0 hidden md:block">
            <ExecutiveReportVisual />
          </div>

        </div>
      </div>
    </section>
  );
};

export default Hero;